/**
 * Client entry point — initializes PixiJS application, WebSocket connection,
 * build mode, and the main game loop.
 */

import { Application, Container } from 'pixi.js';
import { GameWebSocketClient } from './network_websocket_client';
import { GameClientWorldState } from './game_client_world_state';
import { InputMouseKeyboardHandler } from './input_mouse_keyboard_handler';
import { CameraViewportController } from './camera_viewport_controller';
import { RenderMapTerrain } from './render_map_terrain';
import { RenderBuildingSprites } from './render_building_sprites';
import { RenderUnitPuppets } from './render_unit_puppets';
import { UiResourceHudBar } from './ui_resource_hud_bar';
import { UiTechTreePanel, PANEL_WIDTH, PANEL_HEIGHT } from './ui_tech_tree_panel';
import { UiBuildingInfoPanel } from './ui_building_info_panel';
import { UiTransporterRouteConfig } from './ui_transporter_route_config';
import { floatingText } from './render_floating_text';
import { BuildingKind, BuildingState } from './client_message_protocol';
import { UiMobileControls } from './ui_mobile_controls';
import { UiGmCommandPanel } from './ui_gm_command_panel';
import { isMobileDevice, logDeviceInfo } from './device_capability_detector';
import { getDeviceProfile } from './device_profile';

async function main(): Promise<void> {
  const app = new Application();
  await app.init({
    resizeTo: window,
    backgroundColor: 0x0a0a1e,
    antialias: false,
    resolution: 1,
  });

  const isMobile = isMobileDevice();
  logDeviceInfo();
  console.info(`isMobile=${isMobile}`);

  const appElement = document.getElementById('app');
  if (appElement) {
    appElement.appendChild(app.canvas as HTMLCanvasElement);
  }

  const worldLayer = new Container();
  const buildingLayer = new Container();
  const puppetLayer = new Container();
  const uiLayer = new Container();

  app.stage.addChild(worldLayer, buildingLayer, puppetLayer, uiLayer);

  const worldState = new GameClientWorldState();
  const wsClient = new GameWebSocketClient(worldState);
  const input = new InputMouseKeyboardHandler(app.canvas as HTMLCanvasElement);
  const camera = new CameraViewportController(app, input, isMobile);
  const mapRenderer = new RenderMapTerrain(worldLayer);
  const buildingRenderer = new RenderBuildingSprites(buildingLayer);
  const puppetRenderer = new RenderUnitPuppets(puppetLayer);
  // --- HUD bar ---
  const hud = new UiResourceHudBar(uiLayer, app.screen.width, isMobile);

  // --- Floating text init ---
  worldLayer.addChild(floatingText.container);
  floatingText.container.zIndex = 9999;

  // --- Tech tree panel ---
  const techPanel = new UiTechTreePanel(uiLayer, app.screen.width, app.screen.height, wsClient, worldState);

  // --- Building info panel (floating, shown on selection) ---
  const buildingInfoPanel = new UiBuildingInfoPanel(uiLayer);

  // --- Mobile touch controls ---
  let mobileControls: UiMobileControls | null = null;
  if (isMobile) {
    mobileControls = new UiMobileControls(uiLayer, app.screen.width, app.screen.height);
    // Tell HTML debug panel to leave room for the mobile bar
    const profile = getDeviceProfile();
    (window as any)._updateMobileBarHeight?.(profile.bottomBarHeight + profile.safeBottom);
    mobileControls.onBuildingSelected = (kind: BuildingKind) => {
      buildKind = kind;
      buildMode = true;
      hud.buildMode = true;
      hud.buildKind = kind;
    };
    mobileControls.onConsumePill = () => {
      wsClient.send({ type: 'ConsumePill' });
    };
    mobileControls.onConsumeArtifact = () => {
      wsClient.send({ type: 'ConsumeArtifact' });
    };
    mobileControls.onToggleTech = () => {
      techPanel.toggle();
    };
    mobileControls.show();
  }

  // --- GM command panel ---
  const gmPanel = new UiGmCommandPanel();
  gmPanel.setWsClient(wsClient);
  worldState.onGmResult = (message: string) => gmPanel.showResult(message);

  // --- TransporterStation route config modal (lazy init) ---
  let routeConfig: UiTransporterRouteConfig | null = null;

  // --- Selected building state ---
  let selectedBuildingId: number | null = null;

  // --- Build mode state ---
  let buildMode = false;
  const BUILD_KINDS: BuildingKind[] = [
    BuildingKind.SpiritRoot,
    BuildingKind.GatheringArray,
    BuildingKind.HerbFarm,
    BuildingKind.RefiningArray,
    BuildingKind.AlchemyFurnace,
    BuildingKind.Forge,
    BuildingKind.SwordTower,
    BuildingKind.SlowTrap,
    BuildingKind.TransporterStation,
    BuildingKind.DefenseTower,
    BuildingKind.PuppetWorkshop,
    BuildingKind.PuppetBarracks,
  ];
  let buildKind: BuildingKind = BuildingKind.SpiritRoot;

  // --- Debug panel ---
  const w = window as unknown as Record<string, (() => void) | undefined>;
  w._showDebugPanel?.();

  // --- Connect to server ---
  wsClient.connect();

  // --- Join game ---
  wsClient.send({
    type: 'JoinGame',
    player_name: 'Player',
  });

  // --- Draw static map ---
  mapRenderer.draw();

  // --- Camera apply to world layers ---
  const worldContainers = [worldLayer, buildingLayer, puppetLayer];
  function updateCameraTransform(): void {
    for (const c of worldContainers) {
      camera.applyTo(c);
    }
  }
  updateCameraTransform();

  // --- Resource diff tracking for floating text ---
  let prevSpiritQi = 0;
  let prevSpiritStone = 0;
  let prevHerb = 0;
  let prevPill = 0;
  let prevArtifact = 0;

  // --- Main game loop ---
  app.ticker.add(() => {
    const delta = app.ticker.deltaMS / 1000; // seconds

    // Keyboard toggles (one-shot)
    const jp = input.state.keysJustPressed;

    // When GM panel is open, only / and Escape work (close panel)
    if (gmPanel.visible) {
      if (jp.has('/') || jp.has('Escape')) {
        gmPanel.hide();
      }
      // Skip ALL other input when GM panel is open
      input.update();
      return;
    }

    if (!isMobile) {
      if (jp.has('b') || jp.has('B')) {
      buildMode = !buildMode;
      hud.buildMode = buildMode;
      hud.buildKind = buildKind;
    }
    if (jp.has('Escape')) {
      if (techPanel.visible) {
        techPanel.hide();
      } else {
        buildMode = false;
        hud.buildMode = false;
      }
    }
    if (jp.has('g') || jp.has('G')) {
      mapRenderer.toggleGrid();
    }
    if (jp.has('t') || jp.has('T')) {
      techPanel.toggle();
    }
    if (jp.has('f') || jp.has('F')) {
      wsClient.send({ type: 'ConsumeArtifact' });
    }

    // Number keys (1-6) select building kind
    for (let i = 0; i < BUILD_KINDS.length; i++) {
      if (jp.has(String(i + 1))) {
        buildKind = BUILD_KINDS[i];
        hud.buildKind = buildKind;
      }
    }
    // Key '0' selects the 10th building (DefenseTower)
    if (jp.has('0')) {
      buildKind = BUILD_KINDS[9];
      hud.buildKind = buildKind;
    }
    // Key '-' selects the 12th building (PuppetBarracks)
    if (jp.has('-') || jp.has('_')) {
      buildKind = BUILD_KINDS[11];
      hud.buildKind = buildKind;
    }
    } // end if (!isMobile)

    // GM command panel toggle with / key (works on desktop AND mobile)
    if (jp.has('/')) {
      if (!buildMode && !techPanel.visible) {
        gmPanel.toggle();
      }
    }

    // Update camera (pan/drag/zoom)
    camera.update();
    updateCameraTransform();

    // Compute mouse world position
    const world = camera.screenToWorld(input.state.mouseX, input.state.mouseY);
    input.state.mouseWorldX = world.x;
    input.state.mouseWorldY = world.y;

    // Handle build-mode click (skip if tech panel is open and click is on it)
    if (buildMode && input.state.clicked) {
      // Check if tech panel is visible and click is within its bounds
      if (techPanel.visible) {
        const panelWidth = PANEL_WIDTH;
        const panelHeight = PANEL_HEIGHT;
        const panelLeft = app.screen.width / 2 - panelWidth / 2;
        const panelTop = app.screen.height / 2 - panelHeight / 2;
        const clickX = input.state.clickScreenX;
        const clickY = input.state.clickScreenY;
        if (clickX >= panelLeft && clickX <= panelLeft + panelWidth &&
            clickY >= panelTop && clickY <= panelTop + panelHeight) {
          // Click is on the tech panel — don't place a building
          input.update(); // Must update input state to consume the click
          return;
        }
      }
      const clickWorld = camera.screenToWorld(input.state.clickScreenX, input.state.clickScreenY);
      wsClient.send({
        type: 'PlaceBuilding',
        kind: buildKind,
        x: Math.round(clickWorld.x),
        y: Math.round(clickWorld.y),
      });
      console.info(`PlaceBuilding at world (${clickWorld.x.toFixed(0)}, ${clickWorld.y.toFixed(0)})`);
    }

    // Handle building selection click (non build-mode)
    if (!buildMode && input.state.clicked) {
      // Skip if tech panel is visible and click is on it
      let skipClick = false;
      if (techPanel.visible) {
        const panelWidth = PANEL_WIDTH;
        const panelHeight = PANEL_HEIGHT;
        const panelLeft = app.screen.width / 2 - panelWidth / 2;
        const panelTop = app.screen.height / 2 - panelHeight / 2;
        if (input.state.clickScreenX >= panelLeft && input.state.clickScreenX <= panelLeft + panelWidth &&
            input.state.clickScreenY >= panelTop && input.state.clickScreenY <= panelTop + panelHeight) {
          skipClick = true;
        }
      }
      // Also skip if route config modal is visible
      if (routeConfig?.isVisible()) {
        skipClick = true;
      }
      if (!skipClick) {
        const clickWorld = camera.screenToWorld(input.state.clickScreenX, input.state.clickScreenY);
        // Hit-test buildings (32x32 tiles centered on position)
        let hitBuilding: BuildingState | null = null;
        for (const b of worldState.buildings.values()) {
          if (b.player_id !== worldState.localPlayerId) continue;
          const hw = 16; // half tile size
          if (clickWorld.x >= b.position.x - hw && clickWorld.x <= b.position.x + hw &&
              clickWorld.y >= b.position.y - hw && clickWorld.y <= b.position.y + hw) {
            hitBuilding = b;
            break;
          }
        }
        if (hitBuilding) {
          selectedBuildingId = hitBuilding.id;
        } else {
          // Check if config button was hit on the currently visible info panel
          if (buildingInfoPanel.isVisible() &&
              buildingInfoPanel.getConfigButtonHit(input.state.clickScreenX, input.state.clickScreenY)) {
            if (selectedBuildingId !== null) {
              if (!routeConfig) {
                routeConfig = new UiTransporterRouteConfig(
                  uiLayer, app.screen.width, app.screen.height, wsClient, worldState, selectedBuildingId,
                );
              }
              routeConfig.show();
            }
          } else {
            selectedBuildingId = null;
            buildingInfoPanel.hide();
          }
        }
      }
    }

    // Update building info panel
    if (selectedBuildingId !== null) {
      const b = worldState.buildings.get(selectedBuildingId);
      if (b) {
        const screen = camera.worldToScreen(b.position.x, b.position.y);
        buildingInfoPanel.show(b, screen.x, screen.y + 20);
      } else {
        selectedBuildingId = null;
        buildingInfoPanel.hide();
      }
    } else {
      buildingInfoPanel.hide();
    }

    // Detect resource increases for floating text
    if (worldState.spiritQi > prevSpiritQi) {
      const diff = worldState.spiritQi - prevSpiritQi;
      floatingText.show(`+${Math.floor(diff)} Qi`, worldState.spiritQiPos?.x ?? 0, worldState.spiritQiPos?.y ?? 0, 0x44ff44);
    }
    if (worldState.spiritStone > prevSpiritStone) {
      const diff = worldState.spiritStone - prevSpiritStone;
      floatingText.show(`+${Math.floor(diff)} Stone`, worldState.spiritStonePos?.x ?? 0, worldState.spiritStonePos?.y ?? 0, 0x44ff44);
    }
    if (worldState.herb > prevHerb) {
      const diff = worldState.herb - prevHerb;
      floatingText.show(`+${Math.floor(diff)} Herb`, worldState.herbPos?.x ?? 0, worldState.herbPos?.y ?? 0, 0x66cc66);
    }
    if (worldState.pill > prevPill) {
      const diff = worldState.pill - prevPill;
      floatingText.show(`+${Math.floor(diff)} Pill`, worldState.pillPos?.x ?? 0, worldState.pillPos?.y ?? 0, 0xffaa44);
    }
    if (worldState.artifact > prevArtifact) {
      const diff = worldState.artifact - prevArtifact;
      floatingText.show(`+${Math.floor(diff)} Artifact`, worldState.artifactPos?.x ?? 0, worldState.artifactPos?.y ?? 0, 0xff8800);
    }
    prevSpiritQi = worldState.spiritQi;
    prevSpiritStone = worldState.spiritStone;
    prevHerb = worldState.herb;
    prevPill = worldState.pill;
    prevArtifact = worldState.artifact;

    // Process damage events queued by world state
    for (const dmg of worldState.drainDamageQueue()) {
      const target = worldState.buildings.get(dmg.targetId);
      if (target) {
        floatingText.show(`-${dmg.amount}`, target.position.x, target.position.y - 20, 0xff4444);
      }
    }

    // Update floating text animation
    floatingText.update(delta);

    // Render
    buildingRenderer.sync(worldState);
    buildingRenderer.syncHighlight(selectedBuildingId);
    puppetRenderer.sync(worldState);
    hud.update(worldState);

    // Update tech panel every frame (stone count, research progress, flash timer)
    if (techPanel.visible) {
      techPanel.refreshContent();
    }
    techPanel.updateFlash(delta);

    // Resize HUD background on screen resize
    if (hud.screenWidth !== app.screen.width) {
      hud.setScreenWidth(app.screen.width);
    }

    // Resize tech panel on screen resize
    techPanel.handleResize(app.screen.width, app.screen.height);

    // Resize mobile controls on screen resize
    if (mobileControls) {
      mobileControls.handleResize(app.screen.width, app.screen.height);
      const profile = getDeviceProfile();
      (window as any)._updateMobileBarHeight?.(profile.bottomBarHeight + profile.safeBottom);
    }

    // Reset per-frame state at the END (after all checks)
    input.update();
  });

  console.info('Rex client initialized');
}

main().catch((err) => console.error('Fatal init error:', err));
